import React, { useState, useEffect } from 'react';
import { TextField, Button, Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Checkbox, FormControlLabel, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const initialState = {
    estructura: '',
    referencia: '',
    materiaPrima1: '',
    materiaPrima2: '',
    materiaPrima3: '',
    porcentajeMP1: '',
    porcentajeMP2: '',
    porcentajeMP3: '',
    largoMalla: '',
    anchoTela: '',
    pesoMetroCuadrado: '',
    porcentajeEspiralidad: '',
    maquinas: [],
    notas: ''
};

const ReferenciaForm = () => {
    const [form, setForm] = useState(initialState);
    const [referencias, setReferencias] = useState([]);
    const [maquinasDisponibles, setMaquinasDisponibles] = useState([]);
    const [maquinasSeleccionadas, setMaquinasSeleccionadas] = useState({});
    const [materiasPrimas, setMateriasPrimas] = useState([]);
    const [editId, setEditId] = useState(null);
    const [mensaje, setMensaje] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);

    // Cargar referencias y materias primas al montar
    useEffect(() => {
        fetchReferencias();
        fetchMateriasPrimas();
    }, []);

    // Cargar máquinas según la estructura
    useEffect(() => {
        if (form.estructura === 'Interlock' || form.estructura === 'Jersey') {
            fetchMaquinas(form.estructura);
        } else {
            setMaquinasDisponibles([]);
            setMaquinasSeleccionadas({});
        }
    }, [form.estructura]);

    const fetchReferencias = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/referencias`);
            const data = await res.json();
            setReferencias(data.data || data);
        } catch (err) {
            setMensaje('Error al cargar referencias');
        }
    };

    const fetchMateriasPrimas = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/materias-primas`);
            const data = await res.json();
            setMateriasPrimas(data.data || data);
        } catch (err) {
            console.error('Error al cargar materias primas:', err);
        }
    };

    const fetchMaquinas = async (estructura) => {
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            const res = await fetch(`${process.env.REACT_APP_API_URL}/caracteristicas-maquina`, { headers });
            const data = await res.json();
            const maquinasFiltradas = (data.data || data).filter(m => 
                m.subtipoMaquina && m.subtipoMaquina.toUpperCase() === estructura.toUpperCase()
            );
            setMaquinasDisponibles(maquinasFiltradas);
        } catch (err) {
            console.error('Error al cargar máquinas:', err);
            setMensaje('Error al cargar máquinas');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updates = { [name]: value };
        if (name === 'materiaPrima2' && !value) updates.porcentajeMP2 = '';
        if (name === 'materiaPrima3' && !value) updates.porcentajeMP3 = '';
        setForm(prev => ({ ...prev, ...updates }));
    };

    const handleMaquinaToggle = (machineNumber) => {
        setMaquinasSeleccionadas(prev => {
            const newState = { ...prev };
            if (newState[machineNumber]) {
                delete newState[machineNumber];
            } else {
                newState[machineNumber] = { rendimientoPractico: '', revolucionesPorMetro: '' };
            }
            return newState;
        });
    };

    const handleRendimientoChange = (machineNumber, value) => {
        setMaquinasSeleccionadas(prev => ({
            ...prev,
            [machineNumber]: { 
                ...prev[machineNumber],
                rendimientoPractico: value 
            }
        }));
    };

    const handleRevolucionesChange = (machineNumber, value) => {
        setMaquinasSeleccionadas(prev => ({
            ...prev,
            [machineNumber]: { 
                ...prev[machineNumber],
                revolucionesPorMetro: value 
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMensaje('');
        try {
            // Validar porcentajes de materias primas
            const numMPs = [form.materiaPrima1, form.materiaPrima2, form.materiaPrima3].filter(Boolean).length;
            if (numMPs > 1) {
                if (!form.porcentajeMP1) { setMensaje('Ingresa el porcentaje de Materia Prima 1'); return; }
                if (form.materiaPrima2 && !form.porcentajeMP2) { setMensaje('Ingresa el porcentaje de Materia Prima 2'); return; }
                if (form.materiaPrima3 && !form.porcentajeMP3) { setMensaje('Ingresa el porcentaje de Materia Prima 3'); return; }
                const sumaPct = Number(form.porcentajeMP1)
                    + (form.materiaPrima2 ? Number(form.porcentajeMP2) : 0)
                    + (form.materiaPrima3 ? Number(form.porcentajeMP3) : 0);
                if (Math.abs(sumaPct - 100) > 0.01) {
                    setMensaje(`Los porcentajes deben sumar 100. Actualmente suman ${sumaPct.toFixed(1)}%.`);
                    return;
                }
            }
            // Validar que la referencia no esté duplicada (solo al crear, no al editar)
            if (!editId) {
                const referenciaExistente = referencias.find(
                    ref => ref.referencia.toLowerCase() === form.referencia.toLowerCase()
                );
                if (referenciaExistente) {
                    setMensaje('Esta referencia ya existe. Por favor use un nombre diferente.');
                    return;
                }
            }

            const maquinasArray = Object.entries(maquinasSeleccionadas).map(([machineNumber, data]) => ({
                machineNumber,
                rendimientoPractico: Number(data.rendimientoPractico) || 0,
                revolucionesPorMetro: Number(data.revolucionesPorMetro) || 0
            }));

            console.log('=== DEBUG SUBMIT ===');
            console.log('maquinasSeleccionadas:', maquinasSeleccionadas);
            console.log('maquinasArray:', maquinasArray);

            const formToSend = {
                estructura: form.estructura,
                referencia: form.referencia,
                materiaPrima1: form.materiaPrima1,
                materiaPrima2: form.materiaPrima2 || undefined,
                materiaPrima3: form.materiaPrima3 || undefined,
                porcentajeMP1: (form.materiaPrima2 || form.materiaPrima3) && form.porcentajeMP1 ? Number(form.porcentajeMP1) : undefined,
                porcentajeMP2: form.materiaPrima2 && form.porcentajeMP2 ? Number(form.porcentajeMP2) : undefined,
                porcentajeMP3: form.materiaPrima3 && form.porcentajeMP3 ? Number(form.porcentajeMP3) : undefined,
                largoMalla: form.largoMalla ? Number(form.largoMalla) : undefined,
                anchoTela: form.anchoTela ? Number(form.anchoTela) : undefined,
                pesoMetroCuadrado: form.pesoMetroCuadrado ? Number(form.pesoMetroCuadrado) : undefined,
                porcentajeEspiralidad: form.porcentajeEspiralidad ? Number(form.porcentajeEspiralidad) : undefined,
                maquinas: maquinasArray,
                notas: form.notas || undefined
            };

            console.log('formToSend:', JSON.stringify(formToSend, null, 2));

            let res, data;
            if (editId) {
                res = await fetch(`${process.env.REACT_APP_API_URL}/referencias/${editId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formToSend)
                });
                data = await res.json();
            } else {
                res = await fetch(`${process.env.REACT_APP_API_URL}/referencias`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formToSend)
                });
                data = await res.json();
            }
            if (res.ok) {
                setMensaje(editId ? 'Referencia actualizada' : 'Referencia creada correctamente');
                setForm(initialState);
                setMaquinasSeleccionadas({});
                setEditId(null);
                fetchReferencias();
            } else {
                setMensaje(data.message || 'Error al guardar referencia');
            }
        } catch (err) {
            setMensaje('Error de conexión');
        }
    };

    const handleEdit = (ref) => {
        console.log('=== DEBUG EDIT ===');
        console.log('Referencia a editar:', ref);
        
        setForm({
            estructura: ref.estructura || '',
            referencia: ref.referencia || '',
            materiaPrima1: ref.materiaPrima1 || '',
            materiaPrima2: ref.materiaPrima2 || '',
            materiaPrima3: ref.materiaPrima3 || '',
            porcentajeMP1: ref.porcentajeMP1 || '',
            porcentajeMP2: ref.porcentajeMP2 || '',
            porcentajeMP3: ref.porcentajeMP3 || '',
            largoMalla: ref.largoMalla || '',
            anchoTela: ref.anchoTela || '',
            pesoMetroCuadrado: ref.pesoMetroCuadrado || '',
            porcentajeEspiralidad: ref.porcentajeEspiralidad || '',
            notas: ref.notas || ''
        });

        // Reconstruir maquinasSeleccionadas desde el array de máquinas
        const maquinasObj = {};
        if (ref.maquinas && Array.isArray(ref.maquinas)) {
            ref.maquinas.forEach(m => {
                maquinasObj[m.machineNumber] = {
                    rendimientoPractico: m.rendimientoPractico || '',
                    revolucionesPorMetro: m.revolucionesPorMetro || ''
                };
            });
        }
        setMaquinasSeleccionadas(maquinasObj);
                console.log('maquinasObj creado:', maquinasObj);
                setEditId(ref._id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/referencias/${deleteId}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                setMensaje('Referencia eliminada');
                setDeleteId(null);
                setOpenDialog(false);
                fetchReferencias();
            } else {
                setMensaje('Error al eliminar referencia');
            }
        } catch (err) {
            setMensaje('Error de conexión');
        }
    };

    return (
        <div>
            <Paper sx={{ p: 3, maxWidth: 700, margin: 'auto', mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                    {editId ? 'Editar referencia' : 'Crear nueva referencia'}
                </Typography>
                <form onSubmit={handleSubmit}>
    <Grid container spacing={2}>
        <Grid item xs={12}>
            <TextField
                label="Referencia"
                name="referencia"
                value={form.referencia}
                onChange={handleChange}
                required
                fullWidth
            />
        </Grid>
        <Grid item xs={12}>
            <TextField
                select
                label="Estructura"
                name="estructura"
                value={form.estructura}
                onChange={handleChange}
                required
                fullWidth
                SelectProps={{ native: true }}
            >
                <option value=""></option>
                <option value="Interlock">Interlock</option>
                <option value="Jersey">Jersey</option>
            </TextField>
        </Grid>
        {/* ── Materias Primas ── */}
        <Grid item xs={12} sm={4}>
            <TextField
                select
                label="Materia Prima 1"
                name="materiaPrima1"
                value={form.materiaPrima1}
                onChange={handleChange}
                required
                fullWidth
            >
                <MenuItem value="">Seleccionar...</MenuItem>
                {materiasPrimas.map((mp) => (
                    <MenuItem key={mp._id} value={mp.materiaPrima}>{mp.materiaPrima}</MenuItem>
                ))}
            </TextField>
            {(form.materiaPrima2 || form.materiaPrima3) && (
                <TextField
                    label="% Materia Prima 1"
                    name="porcentajeMP1"
                    type="number"
                    value={form.porcentajeMP1}
                    onChange={handleChange}
                    fullWidth
                    required
                    sx={{ mt: 1 }}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    InputLabelProps={{ shrink: true }}
                    placeholder="0 – 100"
                />
            )}
        </Grid>
        <Grid item xs={12} sm={4}>
            <TextField
                select
                label="Materia Prima 2"
                name="materiaPrima2"
                value={form.materiaPrima2}
                onChange={handleChange}
                fullWidth
            >
                <MenuItem value="">Ninguna</MenuItem>
                {materiasPrimas.map((mp) => (
                    <MenuItem key={mp._id} value={mp.materiaPrima}>{mp.materiaPrima}</MenuItem>
                ))}
            </TextField>
            {form.materiaPrima2 && (
                <TextField
                    label="% Materia Prima 2"
                    name="porcentajeMP2"
                    type="number"
                    value={form.porcentajeMP2}
                    onChange={handleChange}
                    fullWidth
                    required
                    sx={{ mt: 1 }}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    InputLabelProps={{ shrink: true }}
                    placeholder="0 – 100"
                />
            )}
        </Grid>
        <Grid item xs={12} sm={4}>
            <TextField
                select
                label="Materia Prima 3"
                name="materiaPrima3"
                value={form.materiaPrima3}
                onChange={handleChange}
                fullWidth
            >
                <MenuItem value="">Ninguna</MenuItem>
                {materiasPrimas.map((mp) => (
                    <MenuItem key={mp._id} value={mp.materiaPrima}>{mp.materiaPrima}</MenuItem>
                ))}
            </TextField>
            {form.materiaPrima3 && (
                <TextField
                    label="% Materia Prima 3"
                    name="porcentajeMP3"
                    type="number"
                    value={form.porcentajeMP3}
                    onChange={handleChange}
                    fullWidth
                    required
                    sx={{ mt: 1 }}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    InputLabelProps={{ shrink: true }}
                    placeholder="0 – 100"
                />
            )}
        </Grid>
        {/* Indicador de suma de porcentajes */}
        {(form.materiaPrima2 || form.materiaPrima3) && (
            <Grid item xs={12}>
                {(() => {
                    const suma = (form.porcentajeMP1 ? Number(form.porcentajeMP1) : 0)
                        + (form.materiaPrima2 && form.porcentajeMP2 ? Number(form.porcentajeMP2) : 0)
                        + (form.materiaPrima3 && form.porcentajeMP3 ? Number(form.porcentajeMP3) : 0);
                    const ok = Math.abs(suma - 100) < 0.01;
                    return (
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: ok ? '#4caf50' : '#f44336' }}>
                            Suma de porcentajes: {suma.toFixed(1)}%{ok ? ' ✓ Correcto' : ' — debe sumar 100%'}
                        </Typography>
                    );
                })()}
            </Grid>
        )}
        <Grid item xs={12} sm={4}>
            <TextField
                label="Largo de Malla"
                name="largoMalla"
                type="number"
                value={form.largoMalla}
                onChange={handleChange}
                fullWidth
                inputProps={{ step: "0.01", min: 0 }}
            />
        </Grid>
        <Grid item xs={12} sm={4}>
            <TextField
                label="Ancho de Tela (cm)"
                name="anchoTela"
                type="number"
                value={form.anchoTela}
                onChange={handleChange}
                fullWidth
                inputProps={{ step: "1", min: 0 }}
                helperText="Ancho en cm para calcular consumo"
            />
        </Grid>
        <Grid item xs={12} sm={4}>
            <TextField
                label="Peso Metro Cuadrado (g/m²)"
                name="pesoMetroCuadrado"
                type="number"
                value={form.pesoMetroCuadrado}
                onChange={handleChange}
                fullWidth
                inputProps={{ step: "1", min: 0 }}
            />
        </Grid>
        <Grid item xs={12} sm={4}>
            <TextField
                label="Porcentaje de Espiralidad (%)"
                name="porcentajeEspiralidad"
                type="number"
                value={form.porcentajeEspiralidad}
                onChange={handleChange}
                fullWidth
                inputProps={{ step: "0.1", min: 0, max: 100 }}
            />
        </Grid>

        {/* Selección de máquinas */}
        {maquinasDisponibles.length > 0 && (
            <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                    Máquinas Disponibles:
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: '#424242' }}>
                    {maquinasDisponibles.map(maquina => (
                        <Box key={maquina.machineNumber} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!maquinasSeleccionadas[maquina.machineNumber]}
                                        onChange={() => handleMaquinaToggle(maquina.machineNumber)}
                                    />
                                }
                                label={`${maquina.machineNumber} - ${maquina.modelo || ''}`}
                                sx={{ minWidth: 250 }}
                            />
                            {maquinasSeleccionadas[maquina.machineNumber] && (
                                <>
                                    <TextField
                                        label="Rendimiento Práctico"
                                        type="number"
                                        value={maquinasSeleccionadas[maquina.machineNumber].rendimientoPractico}
                                        onChange={(e) => handleRendimientoChange(maquina.machineNumber, e.target.value)}
                                        size="small"
                                        sx={{ width: 180 }}
                                        inputProps={{ min: 0 }}
                                    />
                                    <TextField
                                        label="Revoluciones por Metro"
                                        type="number"
                                        value={maquinasSeleccionadas[maquina.machineNumber].revolucionesPorMetro}
                                        onChange={(e) => handleRevolucionesChange(maquina.machineNumber, e.target.value)}
                                        size="small"
                                        sx={{ width: 200 }}
                                        inputProps={{ min: 0 }}
                                    />
                                </>
                            )}
                        </Box>
                    ))}
                </Paper>
            </Grid>
        )}

        <Grid item xs={12}>
            <TextField
                label="Notas"
                name="notas"
                value={form.notas}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={2}
            />
        </Grid>
        <Grid item xs={12}>
            <Button type="submit" variant="contained" color="primary" fullWidth>
                {editId ? 'Actualizar referencia' : 'Guardar referencia'}
            </Button>
        </Grid>
        {mensaje && (
            <Grid item xs={12}>
                <Typography color={mensaje.includes('correctamente') || mensaje.includes('actualizada') || mensaje.includes('eliminada') ? 'green' : 'error'}>
                    {mensaje}
                </Typography>
            </Grid>
        )}
    </Grid>
</form>
            </Paper>

            {/* Tabla de referencias */}
            <TableContainer component={Paper} sx={{ maxWidth: 1400, margin: '40px auto', fontSize: '0.875rem' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Estructura</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Referencia</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Materia Prima 1</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>% MP1</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Materia Prima 2</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>% MP2</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Materia Prima 3</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>% MP3</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Largo Malla</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Ancho (cm)</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Peso m²</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>% Espiralidad</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Máquinas</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Notas</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Fecha Creación</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Editar</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>Eliminar</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {referencias.map((ref) => (
                            <TableRow key={ref._id}>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.estructura || ref.caracteristicasMaquina || ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.referencia}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.materiaPrima1}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.porcentajeMP1 != null ? `${ref.porcentajeMP1}%` : '—'}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.materiaPrima2 || ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.materiaPrima2 && ref.porcentajeMP2 != null ? `${ref.porcentajeMP2}%` : ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.materiaPrima3 || ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.materiaPrima3 && ref.porcentajeMP3 != null ? `${ref.porcentajeMP3}%` : ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.largoMalla || ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.anchoTela ? `${ref.anchoTela} cm` : ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.pesoMetroCuadrado || ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.porcentajeEspiralidad ? `${ref.porcentajeEspiralidad}%` : ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>
                                    {ref.maquinas && ref.maquinas.length > 0 ? (
                                        <Box>
                                            {ref.maquinas.map((m, idx) => (
                                                <Typography key={idx} variant="caption" display="block" sx={{ fontSize: '0.75rem' }}>
                                                    {m.machineNumber}: RP={m.rendimientoPractico} RPM={m.revolucionesPorMetro || 0}
                                                </Typography>
                                            ))}
                                        </Box>
                                    ) : ''}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.notas}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>{ref.fechaCreacion ? new Date(ref.fechaCreacion).toLocaleString() : ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>
                                    <IconButton color="primary" onClick={() => handleEdit(ref)} size="small">
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.8rem' }}>
                                    <IconButton color="error" onClick={() => { setDeleteId(ref._id); setOpenDialog(true); }} size="small">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Diálogo de confirmación para eliminar */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>¿Eliminar referencia?</DialogTitle>
                <DialogContent>
                    ¿Estás seguro de que deseas eliminar esta referencia?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button color="error" onClick={handleDelete}>Eliminar</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ReferenciaForm;